import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BOOKING_STATUS } from "@/lib/seminars";

const EMPTY = { course_id: "", first_name: "", last_name: "", email: "", phone: "", participants: 1, names: "", church: "", notes: "", lunches: 0, status: "confermata", send_email: false };

export default function ManualBookingDialog({ open, onOpenChange, seminars }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sem = seminars.find((s) => s.id === f.course_id);
  const total = (sem?.external_price || 0) * (Number(f.participants) || 0) + (sem?.lunch_price || 0) * (Number(f.lunches) || 0);
  const close = () => { setF(EMPTY); onOpenChange(false); };
  const mut = useMutation({
    mutationFn: async () => (await api.post("/seminar-bookings", {
      ...f, email: f.email || null, phone: f.phone || null, participants: Number(f.participants) || 1,
      lunches: Number(f.lunches) || 0, participant_names: f.names.split(/[\n,]/).map((x) => x.trim()).filter(Boolean),
    })).data,
    onSuccess: () => { toast.success("Iscritto aggiunto"); qc.invalidateQueries({ queryKey: ["seminar-bookings"] }); close(); },
    onError: (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Controlla i dati"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="manual-booking-dialog">
        <DialogHeader>
          <DialogTitle>Aggiungi iscritto esterno</DialogTitle>
          <DialogDescription>Per iscrizioni ricevute di persona, al telefono o via email.</DialogDescription>
        </DialogHeader>
        <div><Label className="text-xs">Seminario *</Label>
          <Select value={f.course_id} onValueChange={(v) => setF({ ...f, course_id: v })}>
            <SelectTrigger className="mt-1" data-testid="manual-booking-course"><SelectValue placeholder="Scegli il seminario"/></SelectTrigger>
            <SelectContent>{seminars.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Nome *</Label><Input value={f.first_name} onChange={set("first_name")} className="mt-1" data-testid="manual-booking-first-name"/></div>
          <div><Label className="text-xs">Cognome *</Label><Input value={f.last_name} onChange={set("last_name")} className="mt-1" data-testid="manual-booking-last-name"/></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={set("email")} className="mt-1" data-testid="manual-booking-email"/></div>
          <div><Label className="text-xs">Telefono</Label><Input value={f.phone} onChange={set("phone")} className="mt-1" data-testid="manual-booking-phone"/></div>
          <div><Label className="text-xs">Persone</Label><Input type="number" min="1" value={f.participants} onChange={set("participants")} className="mt-1" data-testid="manual-booking-participants"/></div>
          <div><Label className="text-xs">Pranzi{sem?.lunch_price ? ` (€ ${Number(sem.lunch_price).toFixed(2)})` : " (non previsti)"}</Label><Input type="number" min="0" disabled={!sem?.lunch_price} value={f.lunches} onChange={set("lunches")} className="mt-1" data-testid="manual-booking-lunches"/></div>
        </div>
        <div><Label className="text-xs">Altri partecipanti (uno per riga)</Label><Textarea rows={2} value={f.names} onChange={set("names")} className="mt-1" data-testid="manual-booking-names"/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Chiesa o ente</Label><Input value={f.church} onChange={set("church")} className="mt-1"/></div>
          <div><Label className="text-xs">Stato</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-1" data-testid="manual-booking-status"><SelectValue/></SelectTrigger>
              <SelectContent>{Object.entries(BOOKING_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label className="text-xs">Note</Label><Input value={f.notes} onChange={set("notes")} className="mt-1"/></div>
        {f.email && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="manual-booking-send-email"/> Invia email di riepilogo all'iscritto
          </label>
        )}
        {sem && <div className="text-sm">Totale: <b data-testid="manual-booking-total">€ {total.toFixed(2)}</b></div>}
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annulla</Button>
          <Button className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!f.course_id || !f.first_name.trim() || !f.last_name.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="manual-booking-save">Aggiungi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
